// @djsem/ui — top-level render error boundary.
//
// Without this, an uncaught render-time throw anywhere in DJSEMWorkspaceApp's module tree (a
// null-deref bug in any one of the ~20 feature modules) unmounts the ENTIRE React tree — the
// whole app goes to a blank white screen with no retry affordance, on both web and desktop.
// This isolates that failure to a single recoverable error screen instead, and gives the user a
// "Reload" affordance rather than a dead app. Only class components can implement
// getDerivedStateFromError/componentDidCatch — there is no hooks equivalent.

import { Component, type ReactNode } from "react";
import { ErrorState } from "./states";

interface Props {
  children: ReactNode;
  /** Called with the caught error so the host app can log it (e.g. to a crash reporter). Errors
   * here must never throw — this runs inside the boundary's own error path. */
  onError?: (error: Error) => void;
}

interface State {
  error: Error | null;
}

export class DJSEMErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error): void {
    try {
      this.props.onError?.(error);
    } catch {
      /* never let a logging failure mask the original render error */
    }
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <ErrorState
          title="Something went wrong"
          detail={this.state.error.message || "An unexpected error occurred while rendering this view."}
          retry={<button type="button" onClick={this.reset}>Try again</button>}
        />
      );
    }
    return this.props.children;
  }
}
